import * as Errors from "../../../shared/node/Errors";

import {
  CreateStripePaymentIntentDocument,
  CreateStripePaymentIntentMutation,
  CreateStripePaymentIntentMutationVariables,
} from "../GeneratedGraphQL";
import { defaultOrganisation, testOrganisation2 } from "./organisation";
import { defaultService, tiers } from "./service";

import { defaultRecurrence } from "./bookings";
import { format } from "date-fns";
import { getBookingQuestionInput } from "../utils";
import { GraphQLError } from "graphql";
import { MockedResponse } from "@apollo/client/testing";
import { successReservedSpaceId } from "./createBookingsForBookingTool";
import { successReservedSpaceWithInvoiceId } from "./createBookingsWithStripeInvoice";
import { today } from "../utils/today";
import { userDetailsStripeInvoice } from "./userDetails";

const timezone = "Europe/London";

const successRequestVariables: CreateStripePaymentIntentMutationVariables = {
  input: {
    bookings: [
      {
        bookingNotes: "Stripe payment notes",
        bookingQuestions: getBookingQuestionInput({
          bookingQuestionData: {},
          bookingQuestions: defaultOrganisation.externalBookingQuestions,
          pickedServiceId: defaultService._id,
        }),
        customer: {
          ...userDetailsStripeInvoice,
          crmFieldData: [
            { id: "date_field", value: format(today, "yyyy-MM-dd") },
            { id: "optional_field", value: "" },
            { id: "text_field", value: "this is a text field" },
          ],
        },
        organisationId: defaultOrganisation._id,
        reservedSpaceId: successReservedSpaceWithInvoiceId,
        timezone: timezone,
      },
    ],
    organisationId: defaultOrganisation._id,
    paymentMethodId: "mockPaymentMethodId",
  },
};

export const createStripePaymentIntentRequestSuccess: MockedResponse<CreateStripePaymentIntentMutation> =
  {
    request: {
      query: CreateStripePaymentIntentDocument,
      variables: successRequestVariables,
    },
    result: {
      data: {
        createStripePaymentIntent: {
          __typename: "StripePaymentIntent",
          clientSecret: "mockClientSecret",
        },
      },
    },
  };

const successMultiTierRequestVariables: CreateStripePaymentIntentMutationVariables =
  {
    input: {
      bookings: [
        {
          bookingQuestions: getBookingQuestionInput({
            bookingQuestionData: {},
            bookingQuestions: testOrganisation2.externalBookingQuestions,
            pickedServiceId: defaultService._id,
          }),
          customer: {
            ...userDetailsStripeInvoice,
            crmFieldData: [
              { id: "date_field", value: format(today, "yyyy-MM-dd") },
              { id: "optional_field", value: "" },
              { id: "text_field", value: "this is a text field" },
            ],
          },
          organisationId: testOrganisation2._id,
          pricePoints: [
            { pricePointId: tiers[0].pricePoints[0]._id, quantity: 2 },
          ],
          reservedSpaceId: successReservedSpaceId,
          tierId: tiers[0]._id,
          timezone: timezone,
        },
      ],
      organisationId: testOrganisation2._id,
      paymentMethodId: "mockPaymentMethodId",
    },
  };

export const createMultiTierStripePaymentIntentRequestSuccess: MockedResponse<CreateStripePaymentIntentMutation> =
  {
    request: {
      query: CreateStripePaymentIntentDocument,
      variables: successMultiTierRequestVariables,
    },
    result: {
      data: {
        createStripePaymentIntent: {
          __typename: "StripePaymentIntent",
          clientSecret: "mockClientSecret",
        },
      },
    },
  };

const recurringBookingRequestVariables: CreateStripePaymentIntentMutationVariables =
  {
    input: {
      bookings: [
        {
          bookingIds: defaultRecurrence.bookings.map((booking) => booking._id),
          bookingQuestions: getBookingQuestionInput({
            bookingQuestionData: {},
            bookingQuestions: testOrganisation2.externalBookingQuestions,
            pickedServiceId: defaultService._id,
          }),
          customer: {
            ...userDetailsStripeInvoice,
            crmFieldData: [
              { id: "date_field", value: format(today, "yyyy-MM-dd") },
              { id: "optional_field", value: "" },
              { id: "text_field", value: "this is a text field" },
            ],
          },
          organisationId: testOrganisation2._id,
          reservedSpaceId: successReservedSpaceWithInvoiceId,
          timezone: timezone,
        },
      ],
      organisationId: testOrganisation2._id,
      paymentMethodId: "mockPaymentMethodId",
    },
  };

export const createStripePaymentIntentRecurringBookingRequestSuccess: MockedResponse<CreateStripePaymentIntentMutation> =
  {
    request: {
      query: CreateStripePaymentIntentDocument,
      variables: recurringBookingRequestVariables,
    },
    result: {
      data: {
        createStripePaymentIntent: {
          __typename: "StripePaymentIntent",
          clientSecret: "mockClientSecret",
        },
      },
    },
  };

const successDiscountRequestVariables: CreateStripePaymentIntentMutationVariables =
  {
    input: {
      ...successRequestVariables.input,
      discount: {
        customerEmail: userDetailsStripeInvoice.email,
        discountCode: "TEST_CODE",
      },
    },
  };

export const createStripeDiscountedPaymentIntentRequestSuccess: MockedResponse<CreateStripePaymentIntentMutation> =
  {
    request: {
      query: CreateStripePaymentIntentDocument,
      variables: successDiscountRequestVariables,
    },
    result: {
      data: {
        createStripePaymentIntent: {
          __typename: "StripePaymentIntent",
          clientSecret: "mockClientSecret",
        },
      },
    },
  };

const successWithCrmRequestVariables: CreateStripePaymentIntentMutationVariables =
  {
    input: {
      bookings: [
        {
          bookingQuestions: getBookingQuestionInput({
            bookingQuestionData: {},
            bookingQuestions: defaultOrganisation.externalBookingQuestions,
            pickedServiceId: defaultService._id,
          }),
          customer: {
            ...userDetailsStripeInvoice,
            crmFieldData: [],
          },
          organisationId: defaultOrganisation._id,
          reservedSpaceId: successReservedSpaceWithInvoiceId,
          timezone: timezone,
        },
      ],
      organisationId: defaultOrganisation._id,
      paymentMethodId: "mockPaymentMethodId",
    },
  };

export const createStripePaymentIntentWithCrmRequestSuccess: MockedResponse<CreateStripePaymentIntentMutation> =
  {
    request: {
      query: CreateStripePaymentIntentDocument,
      variables: successWithCrmRequestVariables,
    },
    result: {
      data: {
        createStripePaymentIntent: {
          __typename: "StripePaymentIntent",
          clientSecret: "mockClientSecret",
        },
      },
    },
  };

const errorRequestVariables: CreateStripePaymentIntentMutationVariables = {
  input: {
    bookings: [
      {
        bookingQuestions: getBookingQuestionInput({
          bookingQuestionData: {},
          bookingQuestions: defaultOrganisation.externalBookingQuestions,
          pickedServiceId: defaultService._id,
        }),
        customer: {
          ...userDetailsStripeInvoice,
          crmFieldData: [
            { id: "date_field", value: format(today, "yyyy-MM-dd") },
            { id: "optional_field", value: "" },
            { id: "text_field", value: "this is a text field" },
          ],
        },
        organisationId: defaultOrganisation._id,
        reservedSpaceId: successReservedSpaceWithInvoiceId,
        timezone: timezone,
      },
    ],
    organisationId: defaultOrganisation._id,
    paymentMethodId: "mockPaymentMethodId",
  },
};

export const createStripePaymentIntentRequestError: MockedResponse<CreateStripePaymentIntentMutation> =
  {
    request: {
      query: CreateStripePaymentIntentDocument,
      variables: errorRequestVariables,
    },
    result: {
      errors: [new GraphQLError("Failed to create payment intent")],
    },
  };

export const invalidOrAlteredCreateStripePaymentIntentRequest: MockedResponse<CreateStripePaymentIntentMutation> =
  {
    request: {
      query: CreateStripePaymentIntentDocument,
      variables: errorRequestVariables,
    },
    result: {
      errors: [new Errors.UserInputError("Something's changed")],
    },
  };
